/**
 * Font loader utility for FF Malmoom font
 */

// Check if a font file exists by trying to fetch it
const checkFontExists = async (url: string): Promise<boolean> => {
  try {
    const response = await fetch(url, { method: 'HEAD' })
    return response.ok
  } catch {
    return false
  }
}

export const loadFFMalmoomFont = async (): Promise<boolean> => {
  try {
    // Check if Font Face API is supported
    if (!('fonts' in document)) {
      console.warn('Font Face API not supported')
      return false
    }

    // First check if any font files exist
    const fontUrls = ['/fonts/FFMalmoom-Regular.woff', '/fonts/FFMalmoom-Regular.eot']

    const fontChecks = await Promise.all(fontUrls.map(checkFontExists))
    const hasAnyFonts = fontChecks.some((exists) => exists)

    if (!hasAnyFonts) {
      console.info('FF Malmoom font files not found. Using fallback fonts.')
      return false
    }

    // Only try to load fonts that exist
    const fontConfigs = [
      { weight: '400', files: ['/fonts/FFMalmoom-Regular.woff', '/fonts/FFMalmoom-Regular.eot'] },
      { weight: '500', files: ['/fonts/FFMalmoom-Regular.woff', '/fonts/FFMalmoom-Regular.eot'] },
      { weight: '700', files: ['/fonts/FFMalmoom-Regular.woff', '/fonts/FFMalmoom-Regular.eot'] },
    ]

    const loadPromises = []

    for (const config of fontConfigs) {
      // Find the first existing file for this weight
      let fontUrl = null
      for (const file of config.files) {
        const index = fontUrls.indexOf(file)
        if (index !== -1 && fontChecks[index]) {
          fontUrl = file
          break
        }
      }

      if (fontUrl) {
        const format = fontUrl.endsWith('.woff')
          ? 'woff'
          : fontUrl.endsWith('.eot')
            ? 'embedded-opentype'
            : 'woff'
        const fontFace = new FontFace('FF Malmoom', `url(${fontUrl}) format("${format}")`, {
          weight: config.weight,
          style: 'normal',
          unicodeRange:
            'U+0600-06FF, U+200C-200E, U+2010-2011, U+204F, U+2E41, U+FB50-FDFF, U+FE70-FEFF',
        })

        loadPromises.push(
          fontFace
            .load()
            .then((loadedFace) => {
              document.fonts.add(loadedFace)
              return true
            })
            .catch((error: Error) => {
              console.warn(`Failed to load FF Malmoom font (${config.weight}):`, error.message)
              return false
            })
        )
      }
    }

    if (loadPromises.length === 0) {
      console.info('No valid FF Malmoom font files found')
      return false
    }

    const results = await Promise.all(loadPromises)
    const anyLoaded = results.some((result) => result)

    if (anyLoaded) {
      console.log('FF Malmoom font loaded successfully')
      document.documentElement.classList.add('ff-malmoom-loaded')
    } else {
      console.info('FF Malmoom fonts could not be loaded. Using fallback fonts.')
    }

    return anyLoaded
  } catch (error) {
    console.info(
      'FF Malmoom font loading skipped:',
      error instanceof Error ? error.message : 'Unknown error'
    )
    return false
  }
}

/**
 * Check if FF Malmoom font is available
 */
export const isFontAvailable = (fontName: string = 'FF Malmoom'): boolean => {
  if (!('fonts' in document)) {
    return false
  }

  return document.fonts.check(`16px "${fontName}"`)
}
